/**
 * This module provides health checks for the user's internet connection.
 *
 * We've found that for some users (some offices, really), the browser's `navigator.onLine`
 * property and related events are not reliable; the browser APIs will say the user is
 * offline, but PostHog recordings will show them continuing to be able to navigate the app.
 *
 * To mitigate this, this module will fall back to hitting Jump's own health check endpoint
 * when the browser says it's offline, and only if that check fails will we consider the user
 * actually offline.
 */
import * as PubSub from '~/pubSub'

const checkUrl = '/_health'
const maxCustomCheckIntervalSeconds = 30

type State = {
  navigatorOnline: boolean
  customCheckTimeout?: ReturnType<typeof setTimeout>
  customCheckFailureCount: number
}

const _state: State = { navigatorOnline: navigator.onLine, customCheckTimeout: undefined, customCheckFailureCount: 0 }

export function isOnline() {
  return navigator.onLine || _state.customCheckFailureCount === 0
}

async function doCustomCheck() {
  const online = await fetch(checkUrl)
  debugLog('Connection to Jump succeeded?', online.ok, online.status)
  const isOnline = online.ok

  const wasOnline = _state.customCheckFailureCount === 0
  _state.customCheckFailureCount = isOnline ? 0 : _state.customCheckFailureCount + 1

  const nextCheckSeconds = isOnline
    ? maxCustomCheckIntervalSeconds
    : exponentialBackoff(1, maxCustomCheckIntervalSeconds, _state.customCheckFailureCount)

  _state.customCheckTimeout = setTimeout(doCustomCheck, nextCheckSeconds * 1_000)

  if (wasOnline !== isOnline) {
    if (isOnline) {
      PubSub.publish('came-online', {})
    } else {
      PubSub.publish('went-offline', {})
    }
  }
}

function exponentialBackoff(base: number, max: number, count: number) {
  return Math.min(base * Math.pow(2, count - 1), max)
}

window.addEventListener('offline', async () => {
  debugLog('Received browser offline event; will attempt custom connectivity check')

  if (!_state.customCheckTimeout) {
    doCustomCheck()
  }
})

window.addEventListener('online', () => {
  if (_state.customCheckTimeout) {
    clearTimeout(_state.customCheckTimeout)
    _state.customCheckTimeout = undefined
    _state.customCheckFailureCount = 0
  }

  PubSub.publish('came-online', {})
})

function debugLog(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.log('[Connectivity]', ...args)
}
